import {HttpClient} from '@angular/common/http';
import {inject} from '@angular/core';
import {catchError, EMPTY, filter, first, map, Observable, of, switchMap,} from 'rxjs';
import {HttpBodyResponse} from 'src/app/core/api-dto/interface/api-http-response.interface';
import {Logger} from 'src/app/core/interfaces/Logger';
import {IQueryParams} from 'src/app/core/interfaces/query-params.interface';
import {environment} from 'src/environments/environment';
import {IAccountApiRepository} from '../../domain/repositories/account-api.repository';
import {IApiAccount} from '../../../../../shared/infrastructure/dto/account-api.interface';
import {AccountFactoryService} from '../../account-factory.service';
import {Account} from '../../domain/model/account.model';
import {AccountStorageMapper} from '../mappers/account-storage.mapper';
import {AccountApiMapper} from '../mappers/account-api.mapper';
import {IApiEntry} from 'src/app/shared/infrastructure/dto/entry-api.interface';
import {Entry} from '../../../entry/domain/model/entry.model';
import {EntryFormattingPort} from 'src/app/shared/domain/ports/entry-formatting.port';
import {ENTRY_FORMATTING_PORT} from 'src/app/shared/domain/tokens/entry-formatting.token';
import {IApiBooking} from "../../../booking/infrastructure/interfaces/booking-api.interface";
import {ISaveAccountEntriesRequest} from "../../application/builders/save-account-entries-request.builder";

export class AccountApiAdapter implements IAccountApiRepository {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(Logger);
  private readonly accountFactoryService = inject(AccountFactoryService);
  private readonly entryFormatting = inject<EntryFormattingPort>(
    ENTRY_FORMATTING_PORT
  );

  /**
   * Retrieves accounts from the server with optional query parameters for filtering, sorting, and including related entities.
   * @param queryParams Optional query parameters for filtering, sorting, and including related entities.
   * @returns Observable emitting an array of Account domain models.
   */
  getAll(queryParams?: IQueryParams): Observable<Account[]> {
    const searchParams = new URLSearchParams();

    searchParams.append('include[accounts]', 'payments,entries');

    if (queryParams?.limit) {
      searchParams.append('limit', queryParams.limit.toString());
    }

    if (queryParams?.skip) {
      searchParams.append('skip', queryParams.skip.toString());
    }

    if (queryParams?.locale) {
      searchParams.append('locale', queryParams.locale);
    }

    if (queryParams?.sort) {
      searchParams.append('sort', queryParams.sort.field);
      searchParams.append('order', queryParams.sort.order);
    }

    if (queryParams?.fields) {
      Object.entries(queryParams.fields).forEach(([entity, fieldList]) => {
        searchParams.append(`fields[${entity}]=`, fieldList.join(','));
      });
    }

    if (queryParams?.include) {
      Object.entries(queryParams.include).forEach(([entity, includeList]) => {
        if (entity !== 'accounts') {
          searchParams.append(`include[${entity}]`, includeList.join(','));
        } else {
          const allIncludes = [
            ...new Set(['payments', 'entries', ...includeList]),
          ];

          searchParams.set('include[accounts]', allIncludes.join(','));
        }
      });
    }

    if (queryParams?.filter) {
      Object.entries(queryParams.filter).forEach(([key, value]) => {
        if (value !== undefined) {
          const stringValue = Array.isArray(value)
            ? value.join(',')
            : String(value);

          searchParams.append(`filter[${key}]`, stringValue);
        }
      });
    }

    const queryString = searchParams.toString();
    const apiUrl = `${environment.apiURL}/accounts${
      queryString ? `?${queryString}` : ''
    }`;

    return this.http.get<HttpBodyResponse<IApiAccount[]>>(apiUrl).pipe(
      map((response: HttpBodyResponse<IApiAccount[]>) => response?.data ?? []),
      switchMap((accounts: IApiAccount[]) =>
        this.accountFactoryService.createMany(
          accounts.map((account) => AccountStorageMapper.fromApi(account))
        )
      ),
      first(),
      catchError((error) => {
        this.logger.error(
          new Error(
            '[AccountApiRepository][getAll] Error fetching accounts from server'
          ),
          error
        );

        return of([]);
      })
    );
  }

  update(account: Partial<Account>): Observable<boolean> {
    const apiUrl = `${environment.apiURL}/api/v2/accounts/${account?.id}`;
    const payload = AccountApiMapper.toPartialApi(account);

    return this.http.put<HttpBodyResponse<IApiAccount>>(apiUrl, payload).pipe(
      map((response: HttpBodyResponse<IApiAccount>) => {
        if (!response) {
          throw new Error('Invalid response from server');
        }

        return !!response.data;
      }),
      first(),
      catchError((error) => {
        this.logger.error(
          new Error('[AccountApiRepository][update] Error updating account'),
          error
        );

        throw error;
      })
    );
  }

  saveEntries(request: ISaveAccountEntriesRequest): Observable<void> {
    const apiUrl = `${environment.apiURL}/api/v2/bookings/${request.bookingId}/entries`;

    return this.http.put<HttpBodyResponse<IApiBooking>>(apiUrl, request).pipe(
      map((response: HttpBodyResponse<IApiBooking>) => {
        if (!response) {
          throw new Error('Invalid response from server');
        }

        return void 0;
      }),
      first(),
      catchError((error) => {
        this.logger.error(
          new Error('[AccountApiRepository][updateEntries] Error updating account entries'),
          error
        );

        throw error;
      })
    );
  }

  getById(accountId: string): Observable<Account> {
    return this.http
      .get<HttpBodyResponse<IApiAccount>>(
        `${environment.apiURL}/accounts/${accountId}?include[accounts]=entries`
      )
      .pipe(
        map((response) => response.data),
        filter(Boolean),
        map((apiAccount) => AccountStorageMapper.fromApi(apiAccount)),
        switchMap((account) => this.accountFactoryService.createOne(account)),
        catchError((error) => {
          this.logger.error(error, `Failed to get account ${accountId}:`);

          return EMPTY;
        })
      );
  }

  getEntriesByAccountIds(accountIds: string[]): Observable<Entry[]> {
    const apiUrl = `${environment.apiURL}/accounts/entries`;

    return this.http
      .post<HttpBodyResponse<IApiEntry[]>>(apiUrl, {account_ids: accountIds})
      .pipe(
        map((response: HttpBodyResponse<IApiEntry[]>) => response?.data ?? []),
        map((entries: IApiEntry[]) =>
          this.entryFormatting.fromDtoToModelList(entries)
        )
      );
  }

  removeAllEntriesFromAccount(entryIds: string[], organizationId: string): Observable<void> {
    const apiUrl = `${environment.apiURL}/api/v2/entries`;

    return this.http
      .delete<HttpBodyResponse<IApiEntry[]>>(apiUrl, {body: {entryIds, organizationId}})
      .pipe(
        map(() => undefined),
        first(),
        catchError((error) => {
          this.logger.error(error, `Failed to remove all entries from account`);

          return EMPTY;
        })
      );
  }
}
